import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiDownload, FiPrinter, FiSearch, FiEye, FiEdit, FiX } from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import CreditApplicationForm from './CreditApplicationForm';
import DealFormsPanel from './DealFormsPanel';
import { DEAL_PACKETS, modelFromDealRow } from '../../config/dealPacketForms';

const FormsManager = () => {
  const [activeTab, setActiveTab] = useState('recent');
  const [deals, setDeals] = useState([]);
  const [financeApps, setFinanceApps] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('');
  const [packetDeal, setPacketDeal] = useState(null);
  const packetModel = useMemo(() => (packetDeal ? modelFromDealRow(packetDeal) : null), [packetDeal]);

  useEffect(() => {
    loadDeals();
    loadFinanceApplications();
  }, []);

  const loadDeals = async () => {
    try {
      const { data, error } = await supabase
        .from('deals')
        .select(`
          *,
          vehicles_db2025!vehicle_id (year, make, model, vin, stock_number)
        `)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      setDeals(data || []);
    } catch (err) {
      console.error('Error loading deals:', err);
    }
  };

  const loadFinanceApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('finance_applications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      setFinanceApps(data || []);
    } catch (err) {
      console.error('Error loading finance applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateForm = (type, dealData) => {
    setFormType(type);
    setSelectedDeal(dealData);
    setShowForm(true);
  };

  const printForm = () => {
    window.print();
  };

  // Purchase Agreement Form
  const PurchaseAgreementForm = ({ deal }) => {
    return (
      <div className="bg-white p-8 max-w-4xl mx-auto" id="printable-form">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold">VEHICLE PURCHASE AGREEMENT</h1>
          <p className="text-sm text-gray-600 mt-2">Paradise-Performance</p>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="font-bold text-sm">BUYER INFORMATION</h3>
            <p className="text-sm">{deal?.customer_name || '_________________'}</p>
            <p className="text-sm">{deal?.customer_phone || '_________________'}</p>
            <p className="text-sm">{deal?.customer_email || '_________________'}</p>
          </div>
          <div>
            <h3 className="font-bold text-sm">VEHICLE INFORMATION</h3>
            <p className="text-sm">
              {deal?.vehicles_db2025 ? 
                `${deal.vehicles_db2025.year} ${deal.vehicles_db2025.make} ${deal.vehicles_db2025.model}` : 
                '_________________'}
            </p>
            <p className="text-sm">VIN: {deal?.vehicles_db2025?.vin || '_________________'}</p>
            <p className="text-sm">Stock #: {deal?.vehicles_db2025?.stock_number || '_________________'}</p>
          </div>
        </div>

        <div className="border-2 border-gray-800 p-4 mb-4">
          <h3 className="font-bold text-sm mb-2">PRICING BREAKDOWN</h3>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Vehicle Price:</span>
              <span>${deal?.vehicle_price || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Trade-In Value:</span>
              <span>-${deal?.trade_in_value || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Trade-In Payoff:</span>
              <span>${deal?.trade_in_payoff || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Down Payment:</span>
              <span>-${deal?.down_payment || 0}</span>
            </div>
            <div className="flex justify-between font-bold border-t pt-2">
              <span>Total Sale Price:</span>
              <span>${deal?.total_price || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Monthly Payment:</span>
              <span>${deal?.monthly_payment?.toFixed(2) || 0}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-8">
          <div className="w-2/5">
            <div className="border-t border-gray-800"></div>
            <p className="text-xs mt-1">Buyer Signature</p>
          </div>
          <div className="w-2/5">
            <div className="border-t border-gray-800"></div>
            <p className="text-xs mt-1">Date</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">Forms & Documents</h1>
      </div>

      {/* Tabs */}
      <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {['recent', 'templates', 'finance-apps'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'recent' && 'Recent Deals'}
                {tab === 'templates' && 'Form Templates'}
                {tab === 'finance-apps' && 'Finance Applications'}
              </button>
            ))}
          </nav>
        </div>

        {/* Recent Deals Tab */}
        {activeTab === 'recent' && (
          <div className="p-6">
            <div className="space-y-4">
              {deals.map((deal) => (
                <div key={deal.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold">{deal.customer_name}</h3>
                      <p className="text-sm text-gray-600">
                        {deal.vehicles_db2025 && 
                          `${deal.vehicles_db2025.year} ${deal.vehicles_db2025.make} ${deal.vehicles_db2025.model}`}
                      </p>
                      <p className="text-xs text-gray-500">Deal ID: {deal.id.slice(0, 8)}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setPacketDeal(deal)}
                        className="px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 font-medium"
                      >
                        Deal Packet (auto-filled)
                      </button>
                      <button
                        onClick={() => generateForm('credit', deal)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Credit App
                      </button>
                      <button
                        onClick={() => generateForm('purchase', deal)}
                        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                      >
                        Purchase Agreement
                      </button>
                      <button
                        onClick={() => generateForm('all', deal)}
                        className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                      >
                        All Forms
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form Templates Tab */}
        {activeTab === 'templates' && (
          <div className="p-6">

            {/* Paradise-Performance deal packets (fillable blanks) */}
            <h3 className="font-semibold text-lg mb-4">Dealership Deal Packets (fillable blanks)</h3>
            <p className="text-sm text-gray-600 mb-4">
              The real Paradise-Performance packets as fillable PDFs. To auto-fill one from a deal, use the
              &ldquo;Deal Packet&rdquo; button on a deal (Recent Deals tab) or the Deal Builder review step.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {DEAL_PACKETS.map((p) => (
                <a
                  key={p.id}
                  href={p.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 border border-primary-300 bg-primary-50 rounded-lg hover:bg-primary-100 text-left block transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <SafeIcon icon={FiDownload} className="w-8 h-8 text-primary-600" />
                    <span className="text-xs text-gray-500">{p.pages} pages</span>
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{p.label}</h3>
                  <p className="text-xs text-gray-600">{p.description}</p>
                </a>
              ))}
            </div>

            {/* Generate Forms from Deal Data */}
            <h3 className="font-semibold text-lg mb-4">Generate Forms from Deal Data</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <button
                onClick={() => generateForm('credit', {})}
                className="p-4 border-2 border-primary-500 bg-primary-50 rounded-lg hover:bg-primary-100 text-left"
              >
                <SafeIcon icon={FiFileText} className="w-8 h-8 text-primary-600 mb-2" />
                <h3 className="font-semibold">Credit Application</h3>
                <p className="text-sm text-gray-600">Generate fillable credit application</p>
              </button>
            </div>

            {/* PDF Forms */}
            <h3 className="font-semibold text-lg mb-4">Texas DMV & Legal Forms</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'Bill of Sale', file: 'Bill-of-Sale.pdf', description: 'Standard vehicle bill of sale' },
                { name: 'Texas Bill of Sale', file: 'texas-vehicle-bill-of-sale.pdf', description: 'Texas-specific bill of sale' },
                { name: 'As-Is Bill of Sale', file: 'As-Is-Bill-of-Sale-Template.pdf', description: 'As-is condition sale agreement' },
                { name: 'Vehicle Transfer (VTR-346)', file: 'Motor-vehicle-transfer-VTR-346.pdf', description: 'Official Texas title transfer form' },
                { name: 'Power of Attorney (VTR-271)', file: 'texas-motor-vehicle-power-of-attorney-vtr-271.pdf', description: 'Texas motor vehicle POA' },
                { name: 'Power of Attorney', file: 'Power-of-Attorney-MotorVehicle-Transactions.pdf', description: 'General vehicle transaction POA' },
                { name: 'Down Payment Receipt', file: 'Vehicle-Down-Payment-Receipt-Template.pdf', description: 'Customer down payment receipt' },
                { name: 'Payment Agreement', file: 'Vehicle-Payment-Agreement.pdf', description: 'Vehicle payment terms agreement' },
                { name: 'Auto Repair Contract', file: 'Auto-Repair-Contract.pdf', description: 'Service department repair agreement' },
                { name: 'Personalized Plates Application', file: 'Application-Dealer-PersonalizedPlates.pdf', description: 'Custom license plate application' },
                { name: 'Replacement Plates/Registration', file: 'Replacement-Plates_or_Registration.pdf', description: 'Replace plates or registration' },
              ].map((form) => (
                <a
                  key={form.file}
                  href={`/forms/${form.file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-4 border rounded-lg hover:bg-gray-50 text-left block transition-colors ${form.important ? 'border-primary-500 bg-primary-50' : ''}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <SafeIcon icon={FiDownload} className="w-8 h-8 text-primary-600" />
                    <span className="text-xs text-gray-500">PDF</span>
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{form.name}</h3>
                  <p className="text-xs text-gray-600">{form.description}</p>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Finance Applications Tab */}
        {activeTab === 'finance-apps' && (
          <div className="p-6">
            <div className="space-y-4">
              {financeApps.map((app) => (
                <div key={app.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold">{app.first_name} {app.last_name}</h3>
                      <p className="text-sm text-gray-600">{app.email} | {app.phone}</p>
                      <p className="text-xs text-gray-500">
                        Applied: {new Date(app.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        app.status === 'approved' ? 'bg-green-100 text-green-800' :
                        app.status === 'denied' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {app.status}
                      </span>
                      <button
                        onClick={() => generateForm('credit', app)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        View Application
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Auto-filled deal packet modal */}
      {packetDeal && packetModel && (
        <div className="fixed inset-0 bg-[var(--adm-scrim)] backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-screen overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-semibold">Deal Packet — {packetDeal.customer_name || 'Deal'}</h2>
                <p className="text-sm text-gray-500">
                  {packetDeal.vehicles_db2025
                    ? `${packetDeal.vehicles_db2025.year} ${packetDeal.vehicles_db2025.make} ${packetDeal.vehicles_db2025.model}`
                    : 'No vehicle linked'}
                </p>
              </div>
              <button
                onClick={() => setPacketDeal(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-1"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" /> Close
              </button>
            </div>
            <div className="p-6">
              <DealFormsPanel model={packetModel} />
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-[var(--adm-scrim)] backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-screen overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Document Preview</h2>
              <div className="flex space-x-2">
                <button
                  onClick={printForm}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                >
                  <FiPrinter className="mr-2" />
                  Print
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto">
              {formType === 'credit' && <CreditApplicationForm dealData={selectedDeal} />}
              {formType === 'purchase' && <PurchaseAgreementForm deal={selectedDeal} />}
              {formType === 'all' && (
                <>
                  <CreditApplicationForm dealData={selectedDeal} />
                  <div className="my-8 border-t-2 border-gray-300"></div>
                  <PurchaseAgreementForm deal={selectedDeal} />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsManager;
